import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api';

interface MyTeam {
  id: string;
  name: string;
  slug: string;
  colour: string | null;
  role_in_team: 'player' | 'lead' | 'admin';
  member_count: number;
}
interface EnrichedMember {
  user_id: string;
  handle: string;
  display_name: string;
  email: string;
  is_ignored: boolean;
}
interface KudosEntry { id: string; occurred_at: string; giver: string; note: string | null; }
interface Me { user: { id: string; handle: string; is_ignored?: boolean } }

// Kudos (M4.5): peer recognition. A player gives kudos to a teammate;
// the receiver gets +3 XP to Communication. Capped once per
// giver→receiver per day. The form uses the current user's teams
// from /api/me/teams (so a player who's in multiple teams can pick
// which one to give within), and pulls the enriched members list
// from the team. Ignored members are hidden from the dropdown.
//
// v0.6.2: every `myTeams.data` and `me.data` access goes through
// `?? []` or `?.` so the component renders an empty state when the
// query hasn't resolved, returned an error, or returned a partial
// response (e.g. {user: undefined} / {teams: undefined}). The previous
// code crashed with "can't access property 0, n.data.teams is
// undefined" whenever the cache resolved to an object without the
// expected field. The TS type contract is honoured at compile time
// only; runtime shape can diverge.
export function KudosSection() {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ['me'], queryFn: () => api<Me>('/api/me') });
  const myTeams = useQuery({ queryKey: ['me-teams'], queryFn: () => api<{ teams: MyTeam[] }>('/api/me/teams') });
  const myTeamsList = myTeams.data?.teams ?? [];
  const [teamId, setTeamId] = useState<string>('');
  const activeTeamId = teamId || myTeamsList[0]?.id || '';
  const members = useQuery({
    queryKey: ['team-members', activeTeamId],
    queryFn: () => api<{ members: EnrichedMember[] }>(`/api/teams/${activeTeamId}/members`),
    enabled: !!activeTeamId,
  });
  const received = useQuery({ queryKey: ['me-kudos'], queryFn: () => api<{ kudos: KudosEntry[] }>('/api/me/kudos') });

  const [receiver, setReceiver] = useState('');
  const [note, setNote] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const give = useMutation({
    mutationFn: () => api<{ ok: true }>('/api/me/kudos', { method: 'POST', body: { receiverHandle: receiver, note } }),
    onSuccess: () => {
      setOk('Kudos sent! 💛');
      setErr(null); setReceiver(''); setNote('');
      qc.invalidateQueries({ queryKey: ['me-kudos'] });
    },
    onError: (e) => {
      const msg = e instanceof ApiError ? e.message : 'Failed to send kudos.';
      setErr(msg); setOk(null);
    },
  });

  // Filter: drop ignored users (they don't participate in the kudos
  // game) and the current user (can't give to self). The server
  // enforces both, but client-side filtering gives a cleaner UX.
  //
  // v0.6.2: chain `?.` on `user` too. The api always returns
  // `{user: {...}}` when authenticated, but the TS type is
  // honoured at compile time only — at runtime the query can
  // resolve to an object without a `user` field (e.g. a stale
  // cache entry from a previous session, or an api response
  // shape that diverged from the type contract). The previous
  // `me.data?.user.id` crashed with "can't access property 'id'"
  // whenever `me.data.user` was undefined. Two callers of `.id`
  // here — both safe now.
  const meId = me.data?.user?.id;
  const teammates = useMemo(() => {
    return (members.data?.members ?? []).filter(
      (m) => !m.is_ignored && m.user_id !== meId,
    );
  }, [members.data, meId]);

  if (myTeams.data && myTeamsList.length === 0) {
    return (
      <div className="rounded-md bg-bg-elevated p-3 text-sm text-ink-muted">
        You're not on a team yet. Ask an admin to add you to one to give and receive kudos.
      </div>
    );
  }

  const receivedList = received.data?.kudos ?? [];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2">
        {myTeams.data && myTeamsList.length > 1 ? (
          <div className="space-y-1">
            <label className="label" htmlFor="kudos-team">Team</label>
            <select
              id="kudos-team"
              className="input"
              value={activeTeamId}
              onChange={(e) => { setTeamId(e.target.value); setReceiver(''); }}
            >
              {myTeamsList.map((t) => (
                <option key={t.id} value={t.id}>{t.name} ({t.member_count} members)</option>
              ))}
            </select>
          </div>
        ) : null}
        <div className="space-y-1">
          <label className="label" htmlFor="kudos-receiver">Give kudos to</label>
          <select
            id="kudos-receiver"
            className="input"
            value={receiver}
            onChange={(e) => setReceiver(e.target.value)}
            disabled={!activeTeamId}
          >
            <option value="">— select teammate —</option>
            {teammates.map((m) => (
              <option key={m.user_id} value={m.handle}>{m.display_name} (@{m.handle})</option>
            ))}
          </select>
        </div>
        <div className="space-y-1 flex-1 min-w-[12rem]">
          <label className="label" htmlFor="kudos-note">Note (optional)</label>
          <input
            id="kudos-note"
            className="input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Great work on the release!"
            maxLength={280}
          />
        </div>
        <button
          className="btn-primary"
          onClick={() => give.mutate()}
          disabled={give.isPending || !receiver}
        >
          {give.isPending ? 'Sending…' : '💛 Send kudos'}
        </button>
      </div>
      {err ? <p className="text-sm text-accent-danger" role="alert">{err}</p> : null}
      {ok ? <p className="text-sm text-accent-success" role="status">{ok}</p> : null}

      {received.data && receivedList.length > 0 ? (
        <div>
          <p className="label">Kudos received</p>
          <ul className="mt-1 space-y-1">
            {receivedList.map((k) => (
              <li key={k.id} className="rounded-md bg-bg-elevated px-3 py-1.5 text-sm">
                <span className="text-ink-muted">💛 from {k.giver}</span>
                {k.note ? <span className="ml-1">— “{k.note}”</span> : null}
                <span className="ml-2 text-xs text-ink-dim">{new Date(k.occurred_at).toLocaleDateString()}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="text-xs text-ink-dim">No kudos received yet. Recognize a teammate to spread some 💛.</p>
      )}
    </div>
  );
}